import React, { useEffect, useMemo, useState } from 'react'
import { NewTable } from '../../../components/datatable/NewTable'
import { Button, Card, CardContent, Chip, Grid, Stack, Tooltip } from '@mui/material'
import { CardTitle } from '../../../components/card/NewCard'
import useTitle from '../../../utils/customHook/useTitle'
import i18n from '../../../simple-react-i18n'
import { getFullDate } from '../../../utils/DateUtil'
import SimpleKeyFigurePanel from '../../../components/keyFigures/SimpleKeyFigurePanel'
import DtoKeyFigure from '../../../station/dto/DtoKeyFigure'
import Input from '../../../components/forms/Input'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getSetting } from '../../../utils/SettingUtils'
import { orderBy, upperFirst } from 'lodash'
import useActions from '../../../utils/customHook/useActions'
import CmsAction from '../../../events/actions/CmsAction'
import SuperMultiAutocomplete from '../../../components/forms/SuperMultiAutocomplete'
import moment from 'moment'
import { normalizeString } from '../../../utils/StringUtil'
import LogCmsExport from './LogCMSExport'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import EmailIcon from '@mui/icons-material/Email'
import Checkbox from '../../../components/forms/Checkbox'
import { nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import ProgressBar from '../../../components/progress/ProgressBar'
import { getStatusCms, IconStatusCms } from './utils/CmsUtils'

const ERROR = 'ERREUR'
const SENT = 'ENVOYE'
const IN_PROGRESS = 'EN COURS'
const NO_INFORMATION = 'AUCUNE INFORMATION'

const MAIL = 'MAIL'
const SMS = 'SMS'

const OPTIONS = [
    { label: IN_PROGRESS, theme: 'warningStatus' },
    { label: SENT, theme: 'validStatus' },
    { label: ERROR, theme: 'errorStatus' },
    { label: NO_INFORMATION, theme: '' },
]

const DEFAULT_VALUE_FILTER = {
    isMail: true,
    isSms: true,
}

const StatusFilterChip = ({
    value,
    setFilter,
    color = 'default',
    sx,
}) => {
    const toggle = () => {
        setFilter(prev => {
            const alreadySelected = prev.status?.includes(value)
            return {
                ...prev,
                status: alreadySelected
                    ? prev.status.filter(v => v !== value)
                    : [...(prev.status || []), value],
            }
        })
    }
    return (
        <Chip
            label={value}
            size={'small'}
            color={color}
            sx={{ mr: 1, ...sx }}
            onClick={toggle}
        />
    )
}

StatusFilterChip.propTypes = {
    value: PropTypes.string,
    label: PropTypes.string,
    setFilter: PropTypes.func,
    color: PropTypes.string,
    sx: PropTypes.shape({}),
}

const Filter = ({
    filter,
    setFilter,
}) => {
    return (
        <Card>
            <CardContent>
                <Grid container spacing={1} justifyContent={'space-between'} alignItems='center'>
                    <Grid size={3}>
                        <Input
                            title={i18n.search}
                            value={filter.searchValue}
                            onChange={ v => setFilter(prev => ({ ...prev, searchValue: v })) }
                            onEnterKeyPress={ v => setFilter({ ...filter, searchValue: v }) }
                            data-cy={'search_input'}
                        />
                    </Grid>
                    <Grid size={3}>
                        <SuperMultiAutocomplete
                            options={ OPTIONS }
                            label={i18n.status}
                            values={ filter?.status }
                            noNullValue
                            multiple
                            keyValue={'label'}
                            onChange={v => setFilter(prev => ({ ...prev, status: v })) }
                        />
                    </Grid>
                    <Grid size={1.5} paddingLeft={2}>
                        <Checkbox
                            checked={filter.isMail}
                            label={<EmailIcon />}
                            onChange={() => setFilter(prev => ({ ...prev, isMail: !prev.isMail })) }
                        />
                    </Grid>
                    <Grid size={2.5}>
                        <Checkbox
                            checked={filter.isSms}
                            label={<SmartphoneIcon />}
                            onChange={() => setFilter(prev => ({ ...prev, isSms: !prev.isSms })) }
                        />
                    </Grid>
                    <Grid container size={2} justifyContent='flex-end'>
                        <Button
                            variant='outlined'
                            onClick={() => {
                                setFilter(DEFAULT_VALUE_FILTER)
                            }}
                            data-cy={'reset_button'}
                        >
                            {i18n.reinit}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}
Filter.propTypes = {
    filter: PropTypes.shape({
        searchValue: PropTypes.string,
        status: PropTypes.arrayOf(PropTypes.string),
        isMail: PropTypes.bool,
        isSms: PropTypes.bool,
    }),
    setFilter: PropTypes.func,
}

const SmsMailDashboardApp = ({}) => {
    const dispatch = useDispatch()

    const [dataLoaded, setDataLoaded] = useState(false)
    const [cms, setCms] = useState([])
    const [filter, setFilter] = useState(DEFAULT_VALUE_FILTER)
    const [isOpen, setIsOpen] = useState(false)

    const [showJobExecutionId, setShowJobExecutionId] = useState(0)

    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)


    const HEADERS = [
        { key: 'typeIcon', value: i18n.jobType },
        'status',
        { key: 'dateSend', value: i18n.date },
        { key: 'recipient', value: i18n.destinataire },
        /*
            { key: 'jobexecutionid', value: 'ID d\'exécution de la tâche' },
        */
        { key: 'contactCode', value: i18n.fullName },
        { key: 'supplierContactCode', value: i18n.supplierCode }, //  code provider
        { key: 'supplierGroupCode', value: i18n.supplierCodeGroup },
        { key: 'supplier', value: i18n.provider }, /*
        'updateDate',
        'updateLogin',*/
    ]

    useTitle(() => [{
        title: i18n.smsMail,
        href: '/cms',
    }, {
        title: i18n.dashboard,
        href: 'cms/dashboard',
    }], [])

    useActions(() => {
        return {
            exportList: [{
                onClick: () => setIsOpen(true),
                label: i18n.excel,
            }],
        }
    }, [])

    useEffect(() => {
        dispatch(CmsAction.fetchCmsSend()).then((r) => {
            const formatExpired = r.map(c => {
                const isExpired = moment().diff(moment(c.dateSend), 'minutes') > 5
                if (getStatusCms(c.status, c.dateSend) === IN_PROGRESS && isExpired) {
                    return {
                        ...c,
                        status: ERROR,
                    }
                }
                return c
            })
            setDataLoaded(true)
            setCms(formatExpired)
        })
    }, [])

    const {
        applicationSettings,
    } = useSelector(store => ({
        applicationSettings: store.AdministrationReducer.applicationSettings,
    }), shallowEqual)

    const cmsFormated = useMemo(() => {
        const cmsFilterStatus = filter.status?.length ? cms.filter(c => filter.status.includes(c.status)) : cms
        const formatCMS = cmsFilterStatus?.map(c => {
            return {
                type: c.recipient.includes('@') ? MAIL : SMS,
                typeIcon: c.recipient.includes('@') ? <Tooltip title={i18n.email}><EmailIcon/></Tooltip> :
                    <Tooltip title={'SMS'}><SmartphoneIcon/></Tooltip>,
                ...c,
                contactCode: contacts.find(co => co.id === c.contactCode)?.name ?? i18n.unknownContact,
                dateSend: getFullDate(c.dateSend),
                date: c.dateSend,
                updateDate: getFullDate(c.updateDate),
                valueStatus: c.status,
                status: { value: IconStatusCms(c.status), status, sortValue: c.status },
            }
        })
        const searchValueFormat = normalizeString(filter.searchValue)
        const filterType = formatCMS.filter(f =>
            (filter.isMail && filter.isSms) ||
            (filter.isMail && f.type === MAIL) ||
            (filter.isSms && f.type === SMS),
        )
        return filter.searchValue ? filterType.filter(p => normalizeString([p.dateSend, p.recipient, p.contactCode].join('   ')).includes(searchValueFormat)) : filterType
    }, [cms, filter, contacts])

    const cmsSend = cms.filter(c => c.status === SENT).length
    const cmsNotSend = cms.filter(c => c.status === ERROR).length
    const cmsNoDetail = cms.filter(c => c.status === NO_INFORMATION).length
    const cmsInProgress = cms.filter(c => c.status === IN_PROGRESS).length

    const todayCms = cms.filter(item =>
        item.dateSend >= moment().startOf('day').valueOf() &&
        item.dateSend <= moment().endOf('day').valueOf(),
    ).length

    const weeklyCms = cms.filter(item =>
        item.dateSend >= moment().startOf('isoWeek').valueOf() &&
        item.dateSend <= moment().endOf('isoWeek').valueOf(),
    ).length

    const monthlyCms = cms.filter(item =>
        item.dateSend >= moment().startOf('month').valueOf() &&
        item.dateSend <= moment().endOf('month').valueOf(),
    ).length

    const keyFigureData = [
        new DtoKeyFigure({
            title: i18n.today,
            value: todayCms,
        }),
        new DtoKeyFigure({
            title: i18n.thisWeek,
            value: weeklyCms,
        }),
        new DtoKeyFigure({
            title: i18n.thisMonth,
            value: monthlyCms,
        }),
    ]

    return (
        <>
            <Grid container spacing={1}>
                <Grid size={10}>
                    <Stack spacing={1}>
                        <Card>
                            <div onClick={() => setShowJobExecutionId(prev => prev + 1)}>
                                <CardTitle title={ i18n.generalInfos } />
                            </div>
                            <CardContent>
                                <Grid container>
                                    <Grid size={6}>
                                        <Grid container alignItems='center'>
                                            <Grid size={5} textAlign='right' paddingRight={1}>
                                                <h6 className='bold'>{i18n.setting}</h6>
                                            </Grid>
                                            <Grid size={7}>
                                                <h6>{upperFirst(getSetting(applicationSettings, 'smsSettings'))}</h6>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent>
                                <Grid container spacing={1}>

                                    <Grid size={3}>
                                        <StatusFilterChip
                                            value={ERROR}
                                            color='errorStatus'
                                            filter={filter}
                                            setFilter={setFilter}
                                        />
                                        {cmsNotSend} {i18n.messages}
                                    </Grid>

                                    <Grid size={3}>
                                        <StatusFilterChip
                                            value={IN_PROGRESS}
                                            color='warningStatus'
                                            filter={filter}
                                            setFilter={setFilter}
                                        />
                                        {cmsInProgress} {i18n.messages}
                                    </Grid>

                                    <Grid size={3}>
                                        <StatusFilterChip
                                            value={SENT}
                                            color='validStatus'
                                            filter={filter}
                                            setFilter={setFilter}
                                        />
                                        {cmsSend} {i18n.messages}
                                    </Grid>

                                    <Grid size={3}>
                                        <StatusFilterChip
                                            value={NO_INFORMATION}
                                            color=''
                                            filter={filter}
                                            setFilter={setFilter}
                                        />
                                        {cmsNoDetail} {i18n.messages}
                                    </Grid>

                                </Grid>
                            </CardContent>
                        </Card>
                        <Filter
                            filter={ filter }
                            setFilter={ setFilter }
                        />
                        {!dataLoaded ? (
                            <Card>
                                <CardContent>
                                    <ProgressBar indeterminate />
                                </CardContent>
                            </Card>
                        ) : (
                            <NewTable
                                rows={orderBy(cmsFormated, 'date', 'desc')}
                                headers={showJobExecutionId > 5 ? [ ...HEADERS, 'jobExecutionId'] : HEADERS}
                                rowsPerPageOptions={nbPerPageLabelMedium}
                                /* onClickRow={s => {
                                  dispatch(push(`/cms/${s.idCms}`))
                                  Futur évolution
                                }}*/
                            />
                        )}
                    </Stack>
                </Grid>
                <Grid size={2}>
                    <SimpleKeyFigurePanel
                        data={keyFigureData}
                        oneStatPerLine
                    />
                </Grid>
            </Grid>
            <LogCmsExport
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                cms={orderBy(cmsFormated, 'date', 'desc')}
                headers={[
                    'status',
                    'dateSend',
                    'recipient',
                    'jobexecutionid',
                    'contactCode',
                    'supplierContactCode',
                    'supplierGroupCode',
                    'updateDate',
                    'updateLogin',
                ]}
            />
        </>
    )
}

export default SmsMailDashboardApp